'use client';

import { useEffect, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import { readPersistedRequestId } from '@avodah-inc/ui';

import { getLogger } from '~/lib/logger';

import { VehrProcessing } from './_components/vehr-processing';

const logger = getLogger();

type PublicErrorBoundaryProps = Readonly<{
  error: Error & { digest?: string };
  reset: () => void;
}>;

const getTenantIdFromSearchParams = (
  searchParams: URLSearchParams,
): string | null => {
  const key = Array.from(searchParams.keys()).find(
    (k) => k.toLowerCase() === 'accountid',
  );
  return key ? searchParams.get(key) : null;
};

export default function PublicErrorBoundary({
  error,
}: PublicErrorBoundaryProps) {
  const searchParams = useSearchParams();
  const tenantId = getTenantIdFromSearchParams(searchParams) ?? 'unknown';
  // eslint-disable-next-line react/hook-use-state -- requestId stable for the error render; no setter exposed
  const [requestId] = useState(
    () => readPersistedRequestId() ?? 'missing-request-id',
  );

  useEffect(() => {
    logger.error(
      new Error('Public route: Unhandled error boundary', { cause: error }),
      {
        className: 'PublicErrorBoundary',
        tenantId,
        requestId,
        error: error.message,
        digest: error.digest,
        stack: error.stack,
      },
    );
  }, [error, requestId, tenantId]);

  return <VehrProcessing errorMessage={error.message} />;
}
